import {
  setFormData,
  setValidation,
  setCurrentStep
} from '@/store/slices/rewardsSlice';
import {
  REWARD_TYPES,
  DISABLE_SAVE_DRAFT_REWARD_STATUS,
  REWARD_STATUS
} from '@/utils/constants/rewardsConstants';
import {buildRewardFieldsPayload} from '@/utils/constants/fieldMappings';
import {
  mapRewardType,
  sanitizeRewardTextForPayload,
  getProductsForDisplay,
  getRewardText,
  getImageUrls,
  buildRewardProducts,
  buildIncludedSegmentGroups,
  buildExcludedSegments
} from '@/helpers/rewardsUtils';
import {
  buildInventoryPayload,
  getDiscountAmount,
  getRewardCashAmount
} from '@/utils/rewardPayloadUtils';
import {getISOTimeString, getISOTimeStringForReview} from '@/utils/commons';

const getFormattedPayloadDates = (formData, status) => {
  const isPublish = status === REWARD_STATUS.PUBLISHED;
  const dateFormatter = isPublish
    ? getISOTimeStringForReview
    : getISOTimeString;

  const startDateValue = formData?.startDateDisplay || formData?.startDate;
  const endDateValue = formData?.endDateDisplay || formData?.endDate;

  return {
    formattedStartDate: startDateValue
      ? dateFormatter(startDateValue, false, true)
      : undefined,
    formattedEndDate: endDateValue
      ? dateFormatter(endDateValue, true, false)
      : undefined
  };
};

const getBonusAlertDisplayCount = (formData) => {
  const raw = formData?.alertDisplayCount;
  if (raw === '' || raw === null || raw === undefined) return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

const addDerivedAmountsToPayload = (payload, formData) => {
  const rewardCashAmount = getRewardCashAmount(
    formData?.rewardType,
    formData?.rewardCashAmount,
    formData?.value
  );
  if (rewardCashAmount != null) {
    payload.rewardCashAmount = rewardCashAmount;
  }

  const discountAmount = getDiscountAmount(
    formData?.rewardType,
    formData?.value
  );
  if (discountAmount != null) {
    payload.discountAmount = discountAmount;
  }
};

const removeKeysIfNullish = (payload, keys) => {
  keys.forEach((key) => {
    if (payload[key] == null) {
      delete payload[key];
    }
  });
};

const addIdIfPresent = (payload, rewardId) => {
  if (!rewardId) return;
  payload.id = isNaN(Number(rewardId)) ? String(rewardId) : Number(rewardId);
};

/**
 * Builds the payload for reward API calls
 * @param {Object} params - Payload parameters
 * @param {Object} params.formData - Form data object
 * @param {Array} params.segments - Segments array
 * @param {string} params.status - Reward status (defaults to DRAFT)
 * @returns {Object} Payload object
 */
export const buildRewardPayload = ({
  formData,
  segments,
  status = REWARD_STATUS.DRAFT,
  languages
}) => {
  const rewardProducts = buildRewardProducts(formData, languages);
  const includedSegmentGroups = buildIncludedSegmentGroups(formData, segments);
  const excludedSegments = buildExcludedSegments(formData, segments);
  const imageUrls = getImageUrls(formData);
  const productsForDisplay = getProductsForDisplay(formData);
  const firstProductId = productsForDisplay?.[0]?.value;
  const rewardTextRaw = getRewardText(formData, firstProductId);
  const rewardText = sanitizeRewardTextForPayload(rewardTextRaw, languages);

  // Use centralized field mappings for simple 1:1 fields
  const simpleFields = buildRewardFieldsPayload(formData);

  // Format dates based on status - use getISOTimeStringForReview for publish (like sales targets)
  // and getISOTimeString for draft (like formatDataForStep in sales targets)
  const {formattedStartDate, formattedEndDate} = getFormattedPayloadDates(
    formData,
    status
  );

  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const alertDisplayCount = isBonusProducts
    ? getBonusAlertDisplayCount(formData)
    : null;

  const payload = {
    // Core fields (not in field mappings due to special handling)
    status,
    rewardType: mapRewardType(formData?.rewardType),

    // Spread fields from centralized mappings (includes name)
    ...simpleFields,

    // Complex/computed fields
    rewardProducts,
    includedSegmentGroups,
    excludedSegments,
    defaultImage: false,
    imageUrls,
    rewardText,
    startDate: formattedStartDate,
    endDate: formattedEndDate,

    // Inventory fields - consolidated into single function (handles quota, leftOverInventory, etc.)
    ...buildInventoryPayload(formData),

    // BONUS_PRODUCTS only - alertDisplayCount
    ...(isBonusProducts && {alertDisplayCount})
  };

  addDerivedAmountsToPayload(payload, formData);
  removeKeysIfNullish(payload, [
    'redeemPoints',
    'orderQty',
    'limitOfProductQty'
  ]);

  // Include id for update if present; backend should create when absent
  addIdIfPresent(payload, formData?.rewardId);

  return payload;
};

/**
 * Gets the disable draft flag based on reward status
 * @param {Object} formData - Form data object
 * @returns {boolean} Whether draft saving should be disabled
 */
export const getDisableDraftFlag = (formData) => {
  const statusCode = formData?.status?.toUpperCase?.();
  if (!statusCode) return false;
  return DISABLE_SAVE_DRAFT_REWARD_STATUS.includes(statusCode);
};

/**
 * Creates a handleDuplicate function
 * @param {Object} params - Handler parameters
 * @param {Object} params.formData - Form data object
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - Navigation function
 * @param {Function} params.showToast - Function to show toast notifications
 * @param {string} params.createRoute - Route for creating new reward
 * @returns {Function} handleDuplicate function
 */
export const createHandleDuplicate = ({
  formData,
  dispatch,
  navigate,
  showToast,
  createRoute
}) => {
  return () => {
    if (!formData) {
      showToast(dispatch, 'No reward data to duplicate', 'error');
      return;
    }

    const duplicatedFormData = JSON.parse(JSON.stringify(formData));

    duplicatedFormData.rewardId = null;
    duplicatedFormData.name = null;
    duplicatedFormData.status = null;
    duplicatedFormData.isDuplicateFlow = true;

    // Reset date fields if start date is in the past
    const startDate =
      duplicatedFormData.startDateDisplay || duplicatedFormData.startDate;
    if (startDate && new Date(startDate) <= new Date()) {
      duplicatedFormData.startDate = null;
      duplicatedFormData.endDate = null;
      duplicatedFormData.startDateDisplay = '';
      duplicatedFormData.endDateDisplay = '';
    }

    dispatch(setFormData(duplicatedFormData));
    dispatch(setValidation({nameValid: undefined}));
    dispatch(setCurrentStep(1));
    navigate(createRoute);
  };
};
